import React, { useState } from "react";
import { Form, Modal, Button } from "react-bootstrap";
import axios from "axios";
import { BASE_URL } from "../../../env";

/**
 * Step 2 of the Daily Loan Branch Manager disbursement flow.
 *
 * The customer has two options:
 *   "thumb"  – BM takes a camera photo of an ink-stamped thumb impression
 *   "drawn"  – BM uploads a photo of a drawn signature
 *
 * Both are just image uploads. The customer is not expected to sign on-screen
 * in the web app (that is the mobile app's canvas feature).
 *
 * On confirm calls POST /api/uploadDlSignature (multipart/form-data).
 * Backend writes customerSignatureFile + signatureType.
 * The Generate Document button then becomes visible.
 */
const DlSignatureModal = ({ show, handleClose, memberId, onSuccess }) => {
  const [signatureType, setSignatureType] = useState("thumb");
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleFileChange = (e) => {
    setFile(e.target.files[0] || null);
  };

  const handleConfirm = async () => {
    setError("");

    if (!file) {
      setError("Please select an image file.");
      return;
    }

    setLoading(true);
    try {
      const formData = new FormData();
      formData.append("memberId", memberId);
      formData.append("signatureType", signatureType);
      formData.append("customerSignatureFile", file);

      await axios.post(`${BASE_URL}/api/uploadDlSignature`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      setFile(null);
      handleClose();
      onSuccess();
    } catch (err) {
      const msg =
        err.response?.data?.error ||
        err.response?.data?.details ||
        "Failed to upload signature. Please try again.";
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal show={show} onHide={handleClose}>
      <Modal.Header closeButton>
        <Modal.Title>Daily Loan — Customer Signature</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && <div className="alert alert-danger">{error}</div>}
        <p className="text-muted mb-3">
          Capture the customer's agreement before generating the loan document.
        </p>
        <Form>
          <Form.Group className="mb-3">
            <Form.Label>Signature Type</Form.Label>
            <div>
              <Form.Check
                inline
                type="radio"
                id="sig-thumb"
                label="Thumb Impression"
                value="thumb"
                checked={signatureType === "thumb"}
                onChange={() => setSignatureType("thumb")}
              />
              <Form.Check
                inline
                type="radio"
                id="sig-drawn"
                label="Drawn / Ink Signature"
                value="drawn"
                checked={signatureType === "drawn"}
                onChange={() => setSignatureType("drawn")}
              />
            </div>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>
              Upload Photo <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              type="file"
              accept="image/*"
              onChange={handleFileChange}
            />
            <Form.Text className="text-muted">
              {signatureType === "thumb"
                ? "Take a clear photo of the ink-stamped thumb impression."
                : "Upload a photo or scan of the customer's drawn signature."}
            </Form.Text>
          </Form.Group>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button variant="primary" onClick={handleConfirm} disabled={loading}>
          {loading ? "Uploading…" : "Save Signature"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default DlSignatureModal;
